import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useFlags } from '@project44/feature-flags';
import {
  Provider,
  reactIcons,
  Separator,
  SideNavigation,
  SideNavigationContent,
  SideNavigationFooter,
  SideNavigationHeader,
  SideNavigationMenu,
  SideNavigationMenuGroup,
  SideNavigationMenuItem,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';
import { useUser } from '@project44/ui-utils';

import {
  MOVEMENT_LINKS,
  MOVEMENT_LINKS_analyticsNavAmi1298,
  MOVEMENT_LINKS_WITH_UPSELL,
  UPSELL_LINKS,
} from '../../constants';
import {
  Analyze,
  AppointmentsUpsell,
  Dashboard,
  EcommerceUpsell,
  FacilitiesUpsell,
  Initiate,
  Movement,
  Network,
  P44OS,
  Track,
} from '../../icons';
import { theme } from '../../theme';
import { getNavigationLinks } from '../../utils';

const icons: Record<string, React.ElementType> = {
  analyze: Analyze,
  dashboard: Dashboard,
  initiate: Initiate,
  track: Track,
  network: Network,
  p44os: P44OS,
  ecommerce_upsell: EcommerceUpsell,
  facilities_upsell: FacilitiesUpsell,
  appointments_upsell: AppointmentsUpsell,
};

const additionalRoutes: Record<string, string[]> = {
  '/results': ['^/workspace(/.*)*$'],
  '/vltl-ltl-rates': ['/discover/vltl-ltl-rates'],
};

export const isSelected = (currentPathname: string, url: string) => {
  const match = currentPathname.includes(url);
  const isResultsSubPath = currentPathname.includes('/results/');

  if (isResultsSubPath) {
    return currentPathname.includes('/dashboard')
      ? url.includes('/dashboard')
      : currentPathname.includes(url);
  }

  if (match) return true;

  if (additionalRoutes[url]) {
    return additionalRoutes[url].some((pattern) =>
      currentPathname.match(pattern),
    );
  }

  return false;
};

export function SideBar(props: { toggleTenantSwitcherComponent: () => void }) {
  const { data: user } = useUser({ skipRedirectOn401: true });

  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const flags = useFlags();
  const { analyticsNavAmi1298, featureVoc1382 } = flags ?? {};
  let links = analyticsNavAmi1298
    ? MOVEMENT_LINKS_analyticsNavAmi1298
    : MOVEMENT_LINKS;
  if (featureVoc1382) {
    links = MOVEMENT_LINKS_WITH_UPSELL;
  }

  const navigationLinks = getNavigationLinks({
    user,
    flags,
    links,
  });

  const fullName = user?.getFullName();
  const initials = user?.getTenantInitials();

  const [isOpen, setIsOpen] = React.useState(false);

  const handleClick = React.useCallback(
    (url) => void history.push(url),
    [history],
  );

  const shouldRenderFooter = !!user;

  return (
    <Provider className={theme.className} css={{ height: '100%' }}>
      <SideNavigation
        isOpen={isOpen}
        css={{
          height: '100%',
          width: isOpen ? 240 : 56,
        }}
        onOpenChange={setIsOpen}
      >
        <SideNavigationHeader
          css={{
            '.manifest-navigation-header__button': { position: 'absolute' },
          }}
          logo={<Movement />}
        />
        <SideNavigationContent>
          <SideNavigationMenu>
            {user &&
              navigationLinks.map(({ iconName, label, url, subNavigation }) => {
                const IconComponent = icons[iconName];
                const labelString = t(label);

                return (
                  <React.Fragment key={label}>
                    {!subNavigation && (
                      <SideNavigationMenuItem
                        isSelected={isSelected(location.pathname, url)}
                        label={labelString}
                        startIcon={<IconComponent label={labelString} />}
                        onClick={() => handleClick(url)}
                        data-testid={`side-nav-${iconName}${
                          isSelected(location.pathname, url) ? '-active' : ''
                        }`}
                      />
                    )}
                    {subNavigation && (
                      <SideNavigationMenuGroup
                        label={labelString}
                        startIcon={<IconComponent label={labelString} />}
                        isExpanded={
                          isOpen && isSelected(location.pathname, url)
                        }
                        onClick={() => setIsOpen(true)}
                        data-testid={`side-nav-${iconName}${
                          isSelected(location.pathname, url) ? '-active' : ''
                        }`}
                      >
                        {subNavigation.map(
                          ({ label: subLabel, url: subUrl }) => {
                            return (
                              <SideNavigationMenuItem
                                isSelected={isSelected(
                                  location.pathname,
                                  subUrl,
                                )}
                                key={subLabel}
                                label={t(subLabel)}
                                onClick={() => handleClick(subUrl)}
                              />
                            );
                          },
                        )}
                      </SideNavigationMenuGroup>
                    )}
                  </React.Fragment>
                );
              })}
            {user && featureVoc1382 && (
              <>
                <Separator css={{ margin: '$medium 0' }} />
                {UPSELL_LINKS.map(({ iconName, label, url }) => {
                  const IconComponent = icons[iconName];
                  const labelString = t(label);
                  return (
                    <SideNavigationMenuItem
                      key={label}
                      isSelected={isSelected(location.pathname, url)}
                      label={labelString}
                      startIcon={<IconComponent label={labelString} />}
                      onClick={() => handleClick(url)}
                      data-testid={`side-nav-${iconName}${
                        isSelected(location.pathname, url) ? '-active' : ''
                      }`}
                    />
                  );
                })}
              </>
            )}
          </SideNavigationMenu>
          <SideNavigationMenu>
            <SideNavigationMenuItem
              css={{ color: '$palette-indigo-100' }}
              isSelected={location?.pathname.includes('/settings')}
              label={t('navigation.settings.title')}
              labelProps={{ css: { fontWeight: '$regular' } }}
              onClick={() => handleClick('/settings')}
              startIcon={
                <reactIcons.Settings
                  aria-label={t('navigation.settings.title')}
                  data-testid="navigation-icon-admin"
                />
              }
            />
          </SideNavigationMenu>
        </SideNavigationContent>
        {shouldRenderFooter && (
          <SideNavigationFooter
            onClick={props.toggleTenantSwitcherComponent}
            avatarProps={{ fallback: initials }}
            css={{
              // TODO: move into manifest
              '.manifest-navigation-footer__content': {
                overflow: 'hidden',
              },

              '.manifest-navigation-footer__title': {
                minWidth: 0,
                overflow: 'hidden',
                textAlign: 'initial',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              },
            }}
            title={user?.companyName}
            subTitle={fullName}
            data-testid="sidebar-ui-avatar"
          />
        )}
      </SideNavigation>
    </Provider>
  );
}
